import axios from "axios";
import { OFFLINE_MESSAGE } from "./errorMessage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Offline (Ordre 24-4): a change is never queued and never sent into the void — it is refused at once, with a sentence saying so. Reads
// still go out (the browser may hold them in cache). `navigator.onLine === false` is reliable; a « true » proves nothing, so only false counts.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (typeof navigator !== "undefined" && navigator.onLine === false && method !== "get" && method !== "head") {
    return Promise.reject(Object.assign(new Error("offline"), { offline: true, config }));
  }
  return config;
});

let onAuthFailure = null;
export function setAuthFailureHandler(fn) {
  onAuthFailure = fn;
}

// Told about every 403 so AuthContext can re-read the account's rights: a refusal often means they changed.
let onForbidden = null;
export function setForbiddenHandler(fn) {
  onForbidden = fn;
}

let refreshPromise = null;
const SKIP_REFRESH_PATHS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/forgot-password",
  "/auth/reset-password",
  // A wrong password here is a 401 about the password, not an expired access token (it covers /session/confirm too).
  "/jamora-admin/session",
];

// Silently retries a request once after refreshing the access token on a 401,
// keeping a session alive past the 12h access token without a re-login as
// long as the 7d refresh cookie is still valid.
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error;
    if (response?.status === 403 && onForbidden) onForbidden();
    if (!response || response.status !== 401 || !config) return Promise.reject(error);
    if (SKIP_REFRESH_PATHS.some((p) => (config.url || "").includes(p))) return Promise.reject(error);
    if (config._retry) return Promise.reject(error);
    config._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = api.post("/auth/refresh").finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      return api(config);
    } catch (refreshErr) {
      if (onAuthFailure) onAuthFailure();
      return Promise.reject(error);
    }
  }
);

export default api;

// The server's own message when it gave one; otherwise `fallback`, which callers set to what was being attempted.
// FastAPI answers a string, a business refusal {code, msg, ...} or a validation list [{msg}, ...].
export function formatApiErrorDetail(detail, fallback = "Une erreur est survenue. Réessayez.") {
  if (detail == null || detail === "") return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => (typeof d?.msg === "string" ? d.msg.replace(/^Value error, /, "") : "")).filter(Boolean).join(" ") || fallback;
  if (typeof detail.msg === "string") return detail.msg;
  return fallback;
}

// The one way to read an API error: message for the user, refusal code, version conflict.
export function errorMessage(error, fallback, offline) {
  if (error?.offline) return OFFLINE_MESSAGE; // refused before leaving the browser (see the request interceptor)
  if (offline && error && !error.response) return offline;
  return formatApiErrorDetail(error?.response?.data?.detail, fallback);
}

export function errorCode(error) {
  const detail = error?.response?.data?.detail;
  return detail && typeof detail === "object" && !Array.isArray(detail) ? detail.code || null : null;
}

export function isVersionConflict(error) {
  return error?.response?.status === 409 && errorCode(error) === "version_conflict";
}
